import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Settings, Info, Save, Eye, EyeOff } from 'lucide-react';
import {
  DifficultySettings,
  AlgorithmicComplexity,
  UnityApiKnowledge,
  CodeScope,
  CognitiveLoad,
  DIFFICULTY_AXIS_LABELS,
  UserSettings,
  Language,
  GENERAL_CORRECTION_THRESHOLD,
  ApiResponse
} from '@unity-learning/shared';
import { useUser } from '../hooks/useUser';
import { useLearningMode } from '../contexts/LearningModeContext';
import { getDifficultySettings, getUserSettings, updateDifficultySettings, updateUserSettings } from '../services/api';

interface DifficultySettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSettingsUpdated?: (settings: DifficultySettings) => void;
}

const DifficultySettingsModal: React.FC<DifficultySettingsModalProps> = ({
  isOpen,
  onClose,
  onSettingsUpdated
}) => {
  const { user } = useUser();
  const { isUnityMode, learningMode } = useLearningMode();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [settings, setSettings] = useState<Omit<DifficultySettings, 'userId' | 'updatedAt'>>({
    algorithmicComplexity: AlgorithmicComplexity.BASIC,
    unityApiKnowledge: UnityApiKnowledge.FUNDAMENTAL,
    codeScope: CodeScope.FUNCTION,
    cognitiveLoad: CognitiveLoad.ISOLATED
  });

  const [userSettings, setUserSettings] = useState<UserSettings>({
    showCorrectionButton: true,
    preferredLanguage: Language.FRENCH
  });

  // Charger les paramètres existants
  useEffect(() => {
    if (isOpen && user) {
      fetchSettings();
    }
  }, [isOpen, user]);

  const fetchSettings = async () => {
    if (!user) return;

    setIsLoading(true);
    setError(null);

    try {
      // Fetch difficulty settings
      let difficulty: DifficultySettings;
      try {
        difficulty = await getDifficultySettings(user.id);
      } catch {
        throw new Error('Erreur lors du chargement des paramètres de difficulté');
      }
      if (difficulty) {
        setSettings({
          algorithmicComplexity: difficulty.algorithmicComplexity,
          unityApiKnowledge: difficulty.unityApiKnowledge,
          codeScope: difficulty.codeScope,
          cognitiveLoad: difficulty.cognitiveLoad
        });
      }

      // Fetch user settings — don't throw if they fail to load, use defaults
      try {
        const saved = await getUserSettings(user.id);
        if (saved) {
          // Ensure preferredLanguage has a fallback for backward compatibility
          setUserSettings({
            ...saved,
            preferredLanguage: saved.preferredLanguage || Language.FRENCH
          });
        }
      } catch {
        /* réglages utilisateur indisponibles : valeurs par défaut */
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const saveSettings = async () => {
    if (!user) return;

    setIsSaving(true);
    setError(null);

    try {
      // Save difficulty settings
      let savedDifficulty: DifficultySettings;
      try {
        savedDifficulty = await updateDifficultySettings(user.id, { ...settings, learningMode });
      } catch {
        throw new Error('Erreur lors de la sauvegarde des paramètres de difficulté');
      }

      // Save user settings
      try {
        await updateUserSettings(user.id, userSettings);
      } catch {
        throw new Error('Erreur lors de la sauvegarde des paramètres utilisateur');
      }

      if (savedDifficulty) {
        if (onSettingsUpdated) {
          onSettingsUpdated(savedDifficulty);
        }
        onClose();
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  // Portail obligatoire : le composant est monté dans <header>, qui porte un
  // `backdrop-blur-md`. Un backdrop-filter crée un bloc conteneur pour les
  // descendants en `position: fixed` — sans ce portail l'overlay se centrerait
  // sur les 64px du header et le haut de la modale sortirait de l'écran (cf. D1).
  return createPortal(
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <Settings className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Paramètres des Challenges
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {error && (
              <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
                <strong className="font-medium">Erreur : </strong>
                {error}
              </div>
            )}

            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-gray-500 dark:text-gray-400">Chargement des paramètres...</div>
              </div>
            ) : (
              <div className="space-y-8">
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-start space-x-2">
                    <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-blue-800 dark:text-blue-300">
                      <p className="font-medium mb-1">Configuration personnalisée de difficulté</p>
                      <p>
                        Ces paramètres s'appliquent à tous les nouveaux exercices générés.
                        Vous pouvez les ajuster selon votre niveau et vos objectifs d'apprentissage.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Axe 1: Complexité Algorithmique */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      🧠 Complexité Algorithmique
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      La difficulté logique pure, indépendante {isUnityMode ? 'de Unity' : 'du langage'}
                    </p>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 text-sm space-y-2">
                      <div className="flex justify-between">
                        <span className="font-medium dark:text-gray-200">Séquentiel (0):</span>
                        <span className="text-gray-600 dark:text-gray-400">Une action après l'autre, pas de logique complexe</span>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-500 ml-4">
                        Ex: "Assigner une valeur à une variable puis l'afficher"
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium dark:text-gray-200">Basique (1):</span>
                        <span className="text-gray-600 dark:text-gray-400">Conditions simples, comparaisons directes</span>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-500 ml-4">
                        Ex: "Si la santé est inférieure à 10, afficher un message"
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium dark:text-gray-200">Intermédiaire (2):</span>
                        <span className="text-gray-600 dark:text-gray-400">Boucles simples, logique avec plusieurs étapes</span>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-500 ml-4">
                        Ex: "Parcourir un tableau et modifier chaque élément selon une condition"
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium dark:text-gray-200">Avancé (3):</span>
                        <span className="text-gray-600 dark:text-gray-400">Algorithmes complexes, optimisation, récursion</span>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-500 ml-4">
                        Ex: "Implémenter un système de pathfinding ou une IA comportementale"
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>Séquentiel</span>
                      <span>Avancé</span>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={3}
                      step={1}
                      value={settings.algorithmicComplexity}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        algorithmicComplexity: parseInt(e.target.value) as AlgorithmicComplexity
                      }))}
                      className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div className="text-center">
                      <span className="inline-block px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-sm font-medium">
                        {DIFFICULTY_AXIS_LABELS.algorithmicComplexity[settings.algorithmicComplexity]}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Axe 2: Connaissances Unity API (Unity mode only) */}
                {isUnityMode && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      🎮 Connaissances Unity API
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      Nécessité de connaître des fonctions spécifiques de Unity
                    </p>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 text-sm space-y-2">
                      <div className="flex justify-between">
                        <span className="font-medium dark:text-gray-200">Fondamental (1):</span>
                        <span className="text-gray-600 dark:text-gray-400">API de base que tout développeur Unity connaît</span>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-500 ml-4">
                        Ex: "transform.position, Start(), Update(), Input.GetKey()"
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium dark:text-gray-200">Courant (2):</span>
                        <span className="text-gray-600 dark:text-gray-400">API fréquemment utilisées mais pas évidentes</span>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-500 ml-4">
                        Ex: "GetComponent(), Rigidbody.velocity, OnTriggerEnter()"
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium dark:text-gray-200">Spécifique (3):</span>
                        <span className="text-gray-600 dark:text-gray-400">API spécialisées nécessitant recherche/documentation</span>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-500 ml-4">
                        Ex: "Physics.Raycast(), Animator.SetTrigger(), coroutines avancées"
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>Fondamental</span>
                      <span>Spécifique</span>
                    </div>
                    <input
                      type="range"
                      min={1}
                      max={3}
                      step={1}
                      value={settings.unityApiKnowledge}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        unityApiKnowledge: parseInt(e.target.value) as UnityApiKnowledge
                      }))}
                      className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div className="text-center">
                      <span className="inline-block px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full text-sm font-medium">
                        {DIFFICULTY_AXIS_LABELS.unityApiKnowledge[settings.unityApiKnowledge!]}
                      </span>
                    </div>
                  </div>
                </div>
                )}

                {/* Axe 3: Étendue du Code */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      📏 Étendue du Code
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      La quantité de code à écrire ou modifier
                    </p>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 text-sm space-y-2">
                      <div className="flex justify-between">
                        <span className="font-medium dark:text-gray-200">Une Ligne (0):</span>
                        <span className="text-gray-600 dark:text-gray-400">Une seule instruction, modification simple</span>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-500 ml-4">
                        Ex: "Changer la valeur d'une variable ou appeler une méthode"
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium dark:text-gray-200">Une Fonction (1):</span>
                        <span className="text-gray-600 dark:text-gray-400">Compléter ou créer une méthode courte (2-8 lignes)</span>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-500 ml-4">
                        Ex: "Implémenter une fonction de mouvement simple"
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium dark:text-gray-200">Un Composant (2):</span>
                        <span className="text-gray-600 dark:text-gray-400">Script complet avec plusieurs méthodes</span>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-500 ml-4">
                        Ex: "Créer un script de contrôleur de joueur avec Start(), Update(), etc."
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium dark:text-gray-200">Interaction (3):</span>
                        <span className="text-gray-600 dark:text-gray-400">Plusieurs scripts qui communiquent ensemble</span>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-500 ml-4">
                        Ex: "Système de score avec UI, collectibles et gestionnaire de jeu"
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>Une Ligne</span>
                      <span>Interaction</span>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={3}
                      step={1}
                      value={settings.codeScope}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        codeScope: parseInt(e.target.value) as CodeScope
                      }))}
                      className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div className="text-center">
                      <span className="inline-block px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 rounded-full text-sm font-medium">
                        {DIFFICULTY_AXIS_LABELS.codeScope[settings.codeScope]}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Axe 4: Charge Cognitive */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      🧭 Charge Cognitive
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      Nombre de concepts à gérer simultanément pendant l'exercice
                    </p>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 text-sm space-y-2">
                      <div className="flex justify-between">
                        <span className="font-medium dark:text-gray-200">Un Seul (1):</span>
                        <span className="text-gray-600 dark:text-gray-400">Focus sur UN concept isolé, instructions courtes</span>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-500 ml-4">
                        Ex: "Apprendre uniquement les boucles for" + instructions concises
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium dark:text-gray-200">Quelques-uns (2):</span>
                        <span className="text-gray-600 dark:text-gray-400">2-3 concepts liés, instructions standard</span>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-500 ml-4">
                        Ex: "Boucles + conditions + variables" avec explications normales
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium dark:text-gray-200">Plusieurs (3):</span>
                        <span className="text-gray-600 dark:text-gray-400">Intégration complexe, instructions détaillées</span>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-500 ml-4">
                        Ex: "Physics + Input + UI + GameObjects" avec beaucoup de contexte
                      </div>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                      <div className="flex items-start space-x-2">
                        <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-blue-800">
                          <p className="font-medium">Impact sur les instructions :</p>
                          <p>Plus la charge cognitive est faible, plus les instructions seront courtes et directes pour permettre de commencer rapidement.</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>Un Seul</span>
                      <span>Plusieurs</span>
                    </div>
                    <input
                      type="range"
                      min={1}
                      max={3}
                      step={1}
                      value={settings.cognitiveLoad}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        cognitiveLoad: parseInt(e.target.value) as CognitiveLoad
                      }))}
                      className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div className="text-center">
                      <span className="inline-block px-3 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 rounded-full text-sm font-medium">
                        {DIFFICULTY_AXIS_LABELS.cognitiveLoad[settings.cognitiveLoad]}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* User Preferences Section */}
          <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-gray-50 dark:bg-gray-900">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
              <Settings className="w-5 h-5 mr-2" />
              Préférences Utilisateur
            </h3>

            <div className="space-y-4">
              {/* Correction Button Setting */}
              <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    {userSettings.showCorrectionButton ? (
                      <Eye className="w-5 h-5 text-green-600" />
                    ) : (
                      <EyeOff className="w-5 h-5 text-gray-400" />
                    )}
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      Bouton "Voir la correction"
                    </h4>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Contrôle l'affichage du bouton de correction dans les exercices normaux.
                    Quand activé, le bouton apparaît pour un score de {GENERAL_CORRECTION_THRESHOLD}% ou plus.
                  </p>
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                    <strong>Note:</strong> Cette option n'affecte pas les quêtes, qui ont leurs propres règles.
                  </p>
                </div>

                <div className="ml-4">
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={userSettings.showCorrectionButton}
                      onChange={(e) => setUserSettings(prev => ({
                        ...prev,
                        showCorrectionButton: e.target.checked
                      }))}
                      className="sr-only"
                    />
                    <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${userSettings.showCorrectionButton ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
                      }`}>
                      <div className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${userSettings.showCorrectionButton ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                    </div>
                  </label>
                </div>
              </div>

              {/* Language Selection Setting */}
              <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <div className="w-5 h-5 text-blue-600">🌍</div>
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      Langue d'exercice
                    </h4>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Choisissez la langue pour la génération des exercices, feedback et chat.
                    L'interface reste en français.
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    <strong>Note:</strong> S'applique aux nouveaux exercices uniquement.
                  </p>
                </div>
                <div className="ml-4">
                  <select
                    value={userSettings.preferredLanguage}
                    onChange={(e) => setUserSettings(prev => ({
                      ...prev,
                      preferredLanguage: e.target.value as Language
                    }))}
                    className="pl-3 pr-8 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white appearance-none bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIiIGhlaWdodD0iOCIgdmlld0JveD0iMCAwIDEyIDgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGQ9Ik0xIDFMNiA2TDExIDEiIHN0cm9rZT0iIzY3NjgxYiIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPC9zdmc+')] dark:bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIiIGhlaWdodD0iOCIgdmlld0JveD0iMCAwIDEyIDgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGQ9Ik0xIDFMNiA2TDExIDEiIHN0cm9rZT0iI2ZmZmZmZiIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPC9zdmc+')] bg-no-repeat bg-[right_8px_center]"
                  >
                    <option value={Language.FRENCH}>Français</option>
                    <option value={Language.ENGLISH}>English</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-4 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={saveSettings}
              disabled={isSaving || isLoading}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSaving ? (
                <>
                  <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Sauvegarde...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Sauvegarder
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};


// Fixed JSX structure for language selection
export default DifficultySettingsModal;